import { getCoreUtils, nz } from "../../../Admin/kReusableCoreTS/kCoreHandler";
import { kSettleOnce } from "../../../Admin/kReusableCoreTS/kSettleOnce";
import { AppAccountPost } from "./AppAccountPost";
import { AppOneDrivePending, AppOneDriveRules } from "./AppOneDrive";
import { UnlockStep } from "./AppUnlockRun";
import { AppDropboxUpload } from "./AppDropboxUpload";
import { DropboxIoRules } from "./DropboxIoRules";

// AppOneDriveRun - the browser half of `AppOneDrive` (sessionStorage, the backup upload, one file read).
// ⛔ NO TOKEN EVER REACHES THIS PAGE for a OneDrive archive: the upload goes to a pre-authenticated address for ONE file
// (`/storage` answers it), and each read asks `/onedrive` for that ONE file's bytes (the archive key is the proof, as
// for every other route). See storage-providers.md section 6.

/** The kept record across Microsoft's page. ⚠ Every `sessionStorage` access is wrapped - it THROWS when blocked. */
export class AppOneDrivePendingStore {
    public static Save = (One: AppOneDrivePending): boolean => {
        if (getCoreUtils().KErrorMode === true) { return false; }
        try {
            window.sessionStorage.setItem(AppOneDriveRules.StorageKey, AppOneDriveRules.PendingText(One));
            return true;
        } catch (e) {
            // ERROR-SUPPRESSED-JUSTIFIED: blocked storage (private mode, policy) is the browser's answer, not a defect;
            // the caller then says the step cannot be finished in this browser (fate 1).
            return false;
        }
    };

    /** Read AND delete - the record (which can hold the sign-up secret) never outlives its one use. */
    public static Take = (): AppOneDrivePending | null => {
        if (getCoreUtils().KErrorMode === true) { return null; }
        try {
            const Text: string = nz(window.sessionStorage.getItem(AppOneDriveRules.StorageKey));
            window.sessionStorage.removeItem(AppOneDriveRules.StorageKey);
            if (Text.length === 0) { return null; }
            return AppOneDriveRules.PendingIn(Text, Date.now());
        } catch (e) {
            // ERROR-SUPPRESSED-JUSTIFIED: blocked storage = no record = `LostWhy` is shown (fate 1).
            return null;
        }
    };
}

/** What a reader of one file must offer (`AppDropboxSource`'s readers). */
export interface IAppOneDriveReader {
    Failed: (Why: string) => void;
    NotThere: () => void;
    /** The file's bytes, as the service read them. */
    Bytes: (Data: Uint8Array) => void;
}

/**
 * One file: the SERVICE reads it with its own token and answers its bytes (base64); answered through the reader, once.
 * ⛔ No Graph host is ever contacted from this page for a read - the CSP allows no wildcard (`AppCsp.test.ts`).
 */
export class AppOneDriveFetch {
    public constructor(private Url: string, private Body: string, private Reader: IAppOneDriveReader) {}

    public Start = (): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (this.Url.length === 0 || this.Body.length === 0) { this.Reader.Failed("This page is not signed in to read your OneDrive folder."); return; }
            new AppAccountPost(this.Url, this.Body, this.OnAnswer).Send();
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppOneDriveFetch.Start");
            this.Reader.Failed("That file could not be requested from your OneDrive.");
        }
    };

    /** ⛔ FATE 1 throughout - a missing page or a lost connection must not blank the archive (`AppDropboxRead`'s rule). */
    public OnAnswer = (Status: number, Text: string): void => {
        // ERROR-SUPPRESSED-JUSTIFIED: no KErrorMode guard - the reader waits on this answer.
        try {
            const Raw: Record<string, unknown> = AppOneDriveFetch.ObjectOf(Text);
            if (Raw.missing === true) { this.Reader.NotThere(); return; }
            if (Raw.ok !== true || typeof Raw.data !== "string") {
                let Why: string = "That file could not be read from your OneDrive.";
                if (typeof Raw.why === "string" && (Raw.why as string).length > 0) { Why = Raw.why as string; }
                this.Reader.Failed(Why);
                return;
            }
            this.Reader.Bytes(AppOneDriveFetch.BytesOf(Raw.data as string));
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppOneDriveFetch.OnAnswer");
            this.Reader.Failed("That file could not be read from your OneDrive.");
        }
    };

    /** base64 -> bytes. DOCUMENTED EXCEPTION: pure decode; `atob` of the service's own base64. */
    public static BytesOf = (Data: string): Uint8Array => {
        const Plain: string = atob(nz(Data));
        const Out: Uint8Array = new Uint8Array(Plain.length);
        for (let At: number = 0; At < Plain.length; At = At + 1) { Out[At] = Plain.charCodeAt(At); }
        return Out;
    };

    /** DOCUMENTED EXCEPTION: parses an answer in hand. ERROR-SUPPRESSED-JUSTIFIED: an unreadable answer is `{}` (fate 1). */
    public static ObjectOf = (Text: string): Record<string, unknown> => {
        try {
            const Raw: unknown = JSON.parse(nz(Text));
            if (Raw === null || typeof Raw !== "object") { return {}; }
            return Raw as Record<string, unknown>;
        } catch (e) {
            // ERROR-SUPPRESSED-JUSTIFIED: a proxy page or a cut answer reads as "no file" and is said (fate 1).
            return {};
        }
    };
}

/**
 * The backup to its pre-authenticated upload address in 320 KiB-multiple chunks (`Content-Range`, no `Authorization`).
 * REUSE-JUSTIFIED: kreuse pointed at `kGraphFileUploader`, which drives the session through `kGraphCore` (a signed-in
 * Graph client); this page holds no token, only the session address. ⭐ A `Blob` is sliced, never read into memory.
 */
export class AppOneDriveUpload {
    private Result: kSettleOnce<string>;
    private Sent: number = 0;
    private Total: number;
    /** Kept for `AppMain.OnImportDone`'s shape - OneDrive has no Dropbox `rev`, so it stays "". */
    public Rev: string = "";

    public constructor(private Url: string, private Body: Uint8Array | Blob, private Step: UnlockStep, Done: (Why: string) => void) {
        this.Result = new kSettleOnce<string>(Done);
        this.Total = AppOneDriveUpload.SizeOf(Body);
    }

    /** DOCUMENTED EXCEPTION: reads a size. ERROR-SUPPRESSED-JUSTIFIED: pure helper. */
    public static SizeOf = (Body: Uint8Array | Blob): number => AppDropboxUpload.SizeOf(Body);

    public Start = (): void => {
        if (getCoreUtils().KErrorMode === true) { this.Result.Settle("WACommander cannot send your history just now."); return; }
        try {
            if (/^https:\/\//.test(nz(this.Url)) === false || this.Total <= 0) {
                this.Result.Settle("That upload could not be started. Nothing was changed.");
                return;
            }
            this.Chunk();
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppOneDriveUpload.Start");
            this.Result.Settle("That upload could not be started.");
        }
    };

    private Chunk = (): void => {
        if (getCoreUtils().KErrorMode === true) { this.Result.Settle("WACommander cannot send your history just now."); return; }
        try {
            this.Step("Sending your history to your OneDrive…", DropboxIoRules.Percent(this.Sent, this.Total));
            const End: number = Math.min(this.Sent + AppOneDriveRules.ChunkBytes, this.Total);
            const Req: XMLHttpRequest = new XMLHttpRequest();
            Req.open("PUT", this.Url, true);
            Req.setRequestHeader("Content-Range", AppOneDriveRules.RangeOf(this.Sent, this.Total));
            Req.onload = new AppOneDriveChunkCall(this, Req, End).OnLoad;
            Req.onerror = this.OnLost;
            Req.send(this.Body.slice(this.Sent, End) as XMLHttpRequestBodyInit);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppOneDriveUpload.Chunk");
            this.Result.Settle("The upload stopped part way.");
        }
    };

    /** 202 = send the next piece; 200/201 after the last = done; anything else = the sentence (fate 1). */
    public OnChunk = (Status: number, End: number): void => {
        if (getCoreUtils().KErrorMode === true) { this.Result.Settle("WACommander cannot send your history just now."); return; }
        try {
            if (Status === 202 && End < this.Total) { this.Sent = End; this.Chunk(); return; }
            if ((Status === 200 || Status === 201) && End >= this.Total) { this.Sent = End; this.Result.Settle(""); return; }
            this.Result.Settle("OneDrive did not accept your history (" + String(Status) + "). Nothing was changed - try again.");
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppOneDriveUpload.OnChunk");
            this.Result.Settle("The upload stopped part way.");
        }
    };

    /** ⛔ A dropped connection is not a fault to report (`AppDropboxUpload.OnLost`'s rule). */
    public OnLost = (): void => {
        // ERROR-SUPPRESSED-JUSTIFIED: a phone losing its signal is the network's answer (fate 1), said to the subscriber.
        this.Result.Settle("The connection dropped while sending your history. Nothing was imported - try again.");
    };
}

class AppOneDriveChunkCall {
    public constructor(private Owner: AppOneDriveUpload, private Req: XMLHttpRequest, private End: number) {}
    public OnLoad = (): void => {
        // ERROR-SUPPRESSED-JUSTIFIED: no KErrorMode guard - the upload waits on this; `OnChunk` guards and reports.
        this.Owner.OnChunk(this.Req.status, this.End);
    };
}
